import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useCallback } from 'react';

import { clearPostFormDraft } from '#modules/finder/features/posts/model/postFormStore';
import { endSession } from '#modules/user/features/auth/model/logout';
import { logout as serverLogout } from '#modules/user/features/auth/utils/logout';
import { clearOAuthReturn } from '#modules/user/features/auth/utils/oauthReturnIntent';
import { getMeOptions } from '#modules/user/model/me';
import { clearNativeToken } from '#features/api/utils/nativeAuth';
import { detachPushToken } from '#features/notifications/utils/detachPushToken';
import { leaveVoice } from '#features/voice/model/voice';
import { localStorage } from '#utils/localStorage';

const clearAuthCookieOnClient = () => {
  if (typeof document === 'undefined') {
    return;
  }

  const expiredAt = 'Thu, 01 Jan 1970 00:00:00 GMT';
  const baseCookie = `authToken=; Expires=${expiredAt}; Max-Age=0; Path=/`;

  document.cookie = `${baseCookie}; SameSite=Lax`;
  document.cookie = `${baseCookie}; SameSite=None; Secure`;
};

// useAuth() lives in many components at once, and an expired session makes all of them reach
// for logout in the same tick. Share one run so that becomes a single request instead of one
// per consumer.
let logoutInFlight: Promise<void> | null = null;

export const useAuth = () => {
  const queryClient = useQueryClient();
  const router = useRouter();

  // Both callbacks invalidate the router, which re-renders everything below it. They must keep
  // a stable identity: callers put them in effect dependencies (see useProfile), and a fresh
  // function every render re-runs those effects — with `logout` that means invalidate →
  // re-render → logout again, a loop that pins the main thread and freezes the tab.
  const logout = useCallback(() => {
    if (logoutInFlight) {
      return logoutInFlight;
    }

    logoutInFlight = (async () => {
      // Выбрасываем профиль из кеша, а не помечаем устаревшим: invalidate оставляет данные
      // лежать, и следующий `useQuery(['me'])` в этой же вкладке сперва отдаёт профиль
      // ПРЕДЫДУЩЕГО пользователя. После «удалить аккаунт» это ломало вход: человек вводил
      // пароль живого аккаунта, попадал на /dashboard — и через полсекунды useProfile видел
      // в кеше `deleted: true` от удалённого профиля и разлогинивал его обратно на /sign-in.
      // Со стороны это выглядит как «вход не работает»: несколько попыток подряд отбивает,
      // потом случайно везёт с таймингом (refetch успевает раньше эффекта).
      queryClient.removeQueries({ queryKey: getMeOptions().queryKey });

      // Первым делом рвём голос: пока соединение с медиасервером живо, микрофон открыт и человека
      // слышно в комнате — уже вышедшим из аккаунта. Делать это надо до смерти куки, иначе
      // серверу нечем опознать, кто уходит.
      await leaveVoice(queryClient);

      // Before the cookie dies — the mutation needs the session to know whose token to release.
      await detachPushToken();

      // Сеанс в реестре устройств — тоже до смерти куки: без токена шлюз не узнает, какой именно
      // вход завершать. Лучшее усилие: выход не ждёт удачи и не зависит от неё.
      await endSession();

      try {
        await serverLogout();
      } catch (error) {
        console.error('Failed to logout', error);
      } finally {
        clearAuthCookieOnClient();
        await clearNativeToken();
        localStorage.deleteItem('token');
        // Черновик заявки (текст, настройки) и незабранная записка о возврате с привязки
        // аккаунта переживают выход: без этого следующий, кто войдёт в этой вкладке,
        // открыл бы форму заявки с чужим текстом.
        clearPostFormDraft();
        clearOAuthReturn();
        router.invalidate();
      }
    })().finally(() => {
      logoutInFlight = null;
    });

    return logoutInFlight;
  }, [queryClient, router]);

  // Awaiting the invalidation matters: it is what re-runs the root `beforeLoad`, so the router
  // context stops saying "anonymous" and the `_authed` guard lets the fresh session through.
  // Returning early (as this used to) left callers navigating with the stale context — the guard
  // bounced them to /sign-in, which bounced them on to /dashboard.
  const login = useCallback(
    async (options: { invalidate: boolean } = { invalidate: true }) => {
      queryClient.invalidateQueries({ queryKey: getMeOptions().queryKey });
      if (options?.invalidate !== false) {
        await router.invalidate();
      }
    },
    [queryClient, router]
  );

  return { logout, login };
};
